
import { useState, useEffect } from "react";
import axios from "axios";
import { useHistory, useParams } from "react-router-dom";
import { baseUrl } from "../../config";
import { CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, StarFilled, TrophyFilled } from '@ant-design/icons';
import moment from 'moment';
import userDummy from "../../assets/images/dummy-user.png";
import PdfImage from "../../assets/images/document.png";
import UserPaymentModalHistory from '../../pages/sn_payments/UserPaymentModalHistory';

import {
  Row,
  Col,
  Card,
  Tag,
  Button,
  Descriptions,
  Avatar,
  message, 
  notification, 
  Typography, 
  Modal, 
  Image, 
  Tabs,
  Progress,
  Space,
  Divider,
  Badge,
  Tooltip,
  Statistic
} from "antd";

import {
  FacebookOutlined,
  TwitterOutlined,
  InstagramOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  HomeOutlined,
  IdcardOutlined,
  TeamOutlined,
  BankOutlined,
  FileTextOutlined,
  EyeOutlined,
  DownloadOutlined,
  CrownOutlined,
  StarOutlined,
  SafetyCertificateOutlined,
  ArrowLeftOutlined,
  PrinterOutlined
} from "@ant-design/icons";

import OccupationDetails from "../sn_payments/OccupationDetails";
import FamilyDetails from "../sn_payments/FamilyDetails";
import SocialStatus from "../sn_payments/SocialStatus";

const getLoyaltyBadge = (years) => {
  if (!years || years < 1) return null;
  if (years >= 10) return { label: `${years}yr`, color: '#722ed1', bg: '#f9f0ff', title: `💎 Lifetime Member — ${years} years` };
  if (years >= 5)  return { label: `${years}yr`, color: '#d4a017', bg: '#fffbe6', title: `🥇 Veteran Member — ${years} years` };
  if (years >= 3)  return { label: `${years}yr`, color: '#8c8c8c', bg: '#f5f5f5', title: `🥈 Senior Member — ${years} years` };
  return            { label: `${years}yr`, color: '#8B4513', bg: '#fdf6ec', title: `🥉 Member — ${years} years` };
};

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

function View() {
  const { id } = useParams();
  const history = useHistory();
  const [userData, setUserData] = useState({});
  const [multiDocument, setMultiDocument] = useState([]);
  const [profileCompleteness, setProfileCompleteness] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    if (id) {
      fetchUserDetails();
    }
  }, [id]);

  useEffect(() => {
    calculateProfileCompleteness();
  }, [userData]);

  const handlePermanentDeactivate = async () => {
    setDeactivating(true);
    try {
      const res = await axios.patch(
        `${baseUrl}/users/permanently-deactivate/${id}`,
        { reason: deactivateReason },
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      );
      if (res.data.status) {
        notification.success({ message: 'Member Deactivated', description: res.data.message, placement: 'topRight' });
        setDeactivateModal(false);
        history.push('/users');
      } else {
        notification.error({ message: 'Error', description: res.data.message, placement: 'topRight' });
      }
    } catch (err) {
      notification.error({ message: 'Error', description: err.response?.data?.message || 'Failed to deactivate', placement: 'topRight' });
    } finally {
      setDeactivating(false);
    }
  };

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/users/read/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.status === 200) {
        setUserData(response.data.result);
        setMultiDocument(response.data.result?.uploadDocuments ? JSON.parse(response.data.result?.uploadDocuments) : []);
      } else {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to fetch user details',
          placement: 'topRight'
        });
      }
    } catch (error) {
      console.error("API error:", error);
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to fetch user details',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateProfileCompleteness = () => {
    const requiredFields = [
      userData.name_eng, 
      userData.phone_no, 
      userData.email_address, 
      userData.dob,
      userData.per_address_en, 
      userData.per_district_en, 
      userData.per_state_en,
      userData.maritial_status, 
      userData.caste, 
      userData.profile_img,
      userData.pan_number, 
      userData.aadhar_no,
      userData.father_name_en,
      userData.gotra,
      userData.category
    ];
    
    const filledFields = requiredFields.filter(field => field && field.toString().trim() !== '').length;
    const completeness = Math.round((filledFields / requiredFields.length) * 100);
    setProfileCompleteness(completeness);
  };

  const getStatusConfig = (status) => {
    switch(status?.toLowerCase()) {
      case 'active': 
        return { color: '#52c41a', icon: <CheckCircleOutlined />, text: 'Active', bgColor: '#f6ffed' };
      case 'inactive': 
        return { color: '#ff4d4f', icon: <ExclamationCircleOutlined />, text: 'Inactive', bgColor: '#fff2f0' };
      case 'pending': 
        return { color: '#faad14', icon: <ClockCircleOutlined />, text: 'Pending', bgColor: '#fffbe6' };
      default: 
        return { color: '#d9d9d9', icon: <ExclamationCircleOutlined />, text: 'Unknown', bgColor: '#fafafa' };
    }
  };

  const getCategoryConfig = (category) => {
  // Simply return the category from database as-is, with default styling
  return {
    color: '#7a0000',
    icon: <UserOutlined />,
    text: category || 'No Category',
    bgColor: '#f0f3f7'
  };
};

  const showModal = (image) => {
    setCurrentImage(image || userDummy);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handlePrint = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [overviewRes, paymentsRes] = await Promise.all([
        axios.get(`${baseUrl}/payments/UserPaymentOverView/${id}`, { headers }),
        axios.get(`${baseUrl}/payments/latestUserPayment/${id}?page=1&limit=1000`, { headers }),
      ]);

      const overview = overviewRes.data.result?.PaymentSN || {};
      const payments = paymentsRes.data.result || [];
      const u = userData;

      const field = (val, fallback = '—') => val || fallback;
      const fmt = (d) => d ? moment(d).format('DD MMM YYYY') : '—';
      const fmtMon = (d) => d ? moment(d).format('MMM YYYY') : '—';
      const cur = (v) => v != null ? `₹ ${Number(v).toLocaleString('en-IN')}` : '—';

      const payRows = payments.map((p, i) => `
        <tr>
          <td style="text-align:center">${i + 1}</td>
          <td style="text-align:center">${fmt(p.transaction_date)}</td>
          <td style="font-weight:600;color:#7a0000">${cur(p.amount)}</td>
          <td>${p.bank_account || '—'}</td>
          <td>${p.special_occassion_category || 'Regular'}</td>
          <td>${fmtMon(p.start_month)} – ${fmtMon(p.end_month)}</td>
          <td>${p.utr_number || '—'}</td>
          <td>${p.pyament_mode_type || '—'}</td>
          <td>${p.receipt_no || '—'}</td>
          <td style="font-weight:600;color:${p.payment_virify === 'verified' ? '#389e0d' : p.payment_virify === 'pending' ? '#d46b08' : '#cf1322'}">
            ${(p.payment_virify || 'PENDING').toUpperCase()}
          </td>
        </tr>`).join('');

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
        <title>Member Report — ${u.name_eng || ''}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; font-size: 12px; color: #222; background: #fff; padding: 20px; }
          .org-header { text-align: center; border-bottom: 2px solid #7a0000; padding-bottom: 10px; margin-bottom: 14px; }
          .org-header h1 { font-size: 16px; color: #7a0000; font-weight: 800; }
          .org-header p { font-size: 11px; color: #555; }
          .member-header { display: flex; gap: 16px; align-items: flex-start; background: #f0f3f7; border: 1px solid #dde3ee; border-radius: 6px; padding: 12px; margin-bottom: 14px; }
          .member-header img { width: 80px; height: 80px; object-fit: cover; border-radius: 6px; border: 2px solid #7a0000; }
          .member-header .info h2 { font-size: 16px; color: #7a0000; font-weight: 800; }
          .member-header .info p { font-size: 11px; color: #555; margin-top: 3px; }
          .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; }
          .stat-box { background: #f0f3f7; border: 1px solid #dde3ee; border-radius: 6px; padding: 10px; text-align: center; }
          .stat-box .label { font-size: 10px; color: #6b7b8d; text-transform: uppercase; font-weight: 700; margin-bottom: 4px; }
          .stat-box .value { font-size: 15px; font-weight: 800; color: #7a0000; }
          .section-title { background: #7a0000; color: #fff; font-size: 12px; font-weight: 700; padding: 5px 10px; border-radius: 4px 4px 0 0; margin-bottom: 0; }
          .fields { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px 16px; border: 1px solid #dde3ee; border-top: none; border-radius: 0 0 4px 4px; padding: 10px 12px; margin-bottom: 14px; }
          .field .lbl { font-size: 10px; color: #6b7b8d; text-transform: uppercase; font-weight: 700; margin-bottom: 2px; }
          .field .val { font-size: 12px; font-weight: 600; color: #7a0000; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background: #7a0000; color: #fff; padding: 6px 6px; text-align: left; font-weight: 700; }
          td { padding: 5px 6px; border-bottom: 1px solid #e8edf4; vertical-align: middle; }
          tr:nth-child(even) td { background: #f7f9fc; }
          .footer { margin-top: 16px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #dde3ee; padding-top: 8px; }
          @media print {
            body { padding: 10px; }
            @page { margin: 12mm; }
          }
        </style>
      </head><body>
        <div class="org-header">
          <h1>अखिल भारतीय राजपुरोहित समाज विकास संस्थान</h1>
          <p>समाज शिक्षा निधि समर्पण योजना — ब्रह्मसरोवर, श्री खेतेश्वर ब्रह्मधामतीर्थ, आसोतरा</p>
        </div>

        <div class="member-header">
          <img src="${u.profile_img || ''}" onerror="this.style.display='none'" alt="Photo"/>
          <div class="info">
            <h2>${field(u.name_eng)} ${u.name_hn ? `(${u.name_hn})` : ''}</h2>
            <p>Membership No: <strong>${field(u.membership_no)}</strong> &nbsp;|&nbsp; Status: <strong>${field(u.status)}</strong> &nbsp;|&nbsp; Category: <strong>${field(u.category)}</strong></p>
            <p>Phone: <strong>${field(u.phone_no)}</strong> &nbsp;|&nbsp; Joining Date: <strong>${fmt(u.joining_date)}</strong></p>
            <p>Printed on: ${moment().format('DD MMM YYYY, hh:mm A')}</p>
          </div>
        </div>

        <div class="stats">
          ${overview.payment_commitment !== 'one_time' ? `<div class="stat-box"><div class="label">Monthly Pledge</div><div class="value">${cur(overview.announced_amount)}</div></div>` : `<div class="stat-box"><div class="label">Payment Type</div><div class="value">One-Time</div></div>`}
          <div class="stat-box"><div class="label">Total Received</div><div class="value" style="color:#389e0d">${cur(overview.received_amount)}</div></div>
          ${overview.payment_commitment !== 'one_time' ? `<div class="stat-box"><div class="label">Total Due</div><div class="value" style="color:#cf1322">${cur(overview.due_amount)}</div></div>` : ''}
          <div class="stat-box"><div class="label">Total Payments</div><div class="value">${payments.length}</div></div>
        </div>

        <div class="section-title">Personal Information</div>
        <div class="fields">
          ${[
            ["Father's Name", field(u.father_name_en)],
            ["Date of Birth", fmt(u.dob)],
            ["Marital Status", field(u.maritial_status)],
            ["Caste", field(u.caste)],
            ["Gotra", field(u.gotra)],
            ["Kuldevi", field(u.kuldevi)],
            ["Kulrishi", field(u.kulrishi)],
            ["PAN Number", field(u.pan_number)],
            ["Email", field(u.email_address)],
            ["Alt. Phone 1", field(u.alternate_phone_no)],
            ["Alt. Phone 2", field(u.alternate_phone_no2)],
            ["Remark", field(u.remark)],
          ].map(([l, v]) => `<div class="field"><div class="lbl">${l}</div><div class="val">${v}</div></div>`).join('')}
        </div>

        <div class="section-title">Address</div>
        <div class="fields">
          ${[
            ["Village", field(u.per_village_en)],
            ["Post", field(u.per_post_en)],
            ["Tahsil", field(u.per_tahsil_en)],
            ["District", field(u.per_district_en)],
            ["State", field(u.per_state_en)],
            ["PIN Code", field(u.per_pin_code_en)],
          ].map(([l, v]) => `<div class="field"><div class="lbl">${l}</div><div class="val">${v}</div></div>`).join('')}
        </div>

        <div class="section-title" style="margin-bottom:6px">Payment History (${payments.length} records)</div>
        <table>
          <thead>
            <tr>
              <th>#</th><th>Date</th><th>Amount</th><th>Account</th>
              <th>Category</th><th>Period</th><th>UTR</th>
              <th>Mode</th><th>Receipt No</th><th>Status</th>
            </tr>
          </thead>
          <tbody>${payRows || '<tr><td colspan="10" style="text-align:center;color:#999;padding:12px">No payments found</td></tr>'}</tbody>
        </table>

        <div class="footer">
          Generated by SN-Abrsvs Admin &nbsp;|&nbsp; ${moment().format('DD MMM YYYY, hh:mm A')}
        </div>
      </body></html>`;

      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 400);
    } catch (err) {
      notification.error({ message: 'Print failed', description: err.message });
    }
  };

  const statusConfig = getStatusConfig(userData.status);
  const categoryConfig = getCategoryConfig(userData.category);

  const renderBilingualField = (enField, hnField, fallback = 'Not provided') => {
    if (!enField && !hnField) return fallback;
    return (
      <div>
        <Text>{enField || hnField}</Text>
        {enField && hnField && enField !== hnField && (
          <Text type="secondary" style={{ display: 'block', fontSize: '13px' }}>
            {hnField}
          </Text>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            <UserOutlined />
          </div>
          <Text>Loading member profile...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f0f3f7', minHeight: '100vh' }}>
      {/* Simple Header Bar */}
      <div style={{ background: '#7a0000', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        {/* Avatar + Name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 200 }}>
          <Badge
            count={<span style={{ color: statusConfig.color, fontSize: 16 }}>{statusConfig.icon}</span>}
            offset={[-4, 4]}
          >
            <Avatar
              size={56}
              src={userData.profile_img || userDummy}
              icon={<UserOutlined />}
              onClick={() => showModal(userData.profile_img || userDummy)}
              style={{ cursor: 'pointer', border: '2px solid rgba(255,255,255,0.5)' }}
            />
          </Badge>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>{userData.name_eng || 'Member Name'}</span>
              {userData.account_count >= 2 && (
                <Tooltip title={`Contributing to ${userData.account_count} accounts`}>
                  <StarFilled style={{ color: '#faad14', fontSize: 14 }} />
                </Tooltip>
              )}
              {(() => {
                const badge = getLoyaltyBadge(userData.years_active);
                if (!badge) return null;
                return (
                  <Tooltip title={badge.title}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: badge.color, background: badge.bg, border: `1px solid ${badge.color}`, borderRadius: 4, padding: '0 4px', lineHeight: '16px' }}>
                      <TrophyFilled style={{ fontSize: 9, marginRight: 2 }} />{badge.label}
                    </span>
                  </Tooltip>
                );
              })()}
            </div>
            {userData.name_hn && <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>{userData.name_hn}</div>}
          </div>
        </div>

        {/* Quick Info */}
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}><PhoneOutlined style={{ marginRight: 5 }} />{userData.phone_no || '-'}</span>
          <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}><MailOutlined style={{ marginRight: 5 }} />{userData.email_address || 'No Email'}</span>
          <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}><CalendarOutlined style={{ marginRight: 5 }} />Since {userData?.joining_date ? moment(userData.joining_date).format('DD MMM YYYY') : '-'}</span>
        </div>

        {/* Status Pills + Back Button */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto', flexWrap: 'wrap' }}>
          <Tag style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 20, padding: '2px 12px', fontSize: 12, fontWeight: 600 }}>
            <span style={{ color: statusConfig.color, marginRight: 5 }}>{statusConfig.icon}</span>{statusConfig.text}
          </Tag>
          {userData.caste && <Tag style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 20, padding: '2px 12px', fontSize: 12 }}>{userData.caste}</Tag>}
          <Tag style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 20, padding: '2px 12px', fontSize: 12 }}>
            Profile {profileCompleteness}%
          </Tag>
          <Tag style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 20, padding: '2px 12px', fontSize: 12 }}>
            <FileTextOutlined style={{ marginRight: 4 }} />{multiDocument.length} Docs
          </Tag>
          <Button
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            style={{ background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.5)', color: '#fff', borderRadius: 6, fontWeight: 600 }}
            size="small"
          >
            Print
          </Button>
          <Button
            danger
            size="small"
            style={{ borderRadius: 6, fontWeight: 600, background: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' }}
            onClick={() => { setDeactivateReason(''); setDeactivateModal(true); }}
          >
            Deactivate
          </Button>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => history.goBack()}
            style={{ background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.5)', color: '#fff', borderRadius: 6, fontWeight: 600 }}
            size="small"
          >
            Back
          </Button>
        </div>
      </div>

      {/* Permanently Deactivate Confirmation Modal */}
      <Modal
        open={deactivateModal}
        onCancel={() => setDeactivateModal(false)}
        onOk={handlePermanentDeactivate}
        okText="Yes, Permanently Deactivate"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: deactivating, disabled: !deactivateReason.trim() }}
        title={<span style={{ color: '#cf1322', fontWeight: 700 }}>⚠️ Permanently Deactivate Member</span>}
        centered
        width={480}
        destroyOnClose
      >
        <div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, color: '#cf1322', marginBottom: 6 }}>This will:</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: '#595959', fontSize: 13, lineHeight: 2 }}>
            <li>Hide this member from <strong>all records, lists and reports</strong></li>
            <li>Block <strong>all WhatsApp messages</strong> to this member</li>
            <li>Remove from <strong>voice call campaigns</strong></li>
            <li>Stop all <strong>payment reminders</strong></li>
          </ul>
          <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>This can be reversed by an admin if needed.</div>
        </div>
        <div style={{ marginBottom: 8, fontWeight: 600 }}>
          Reason for deactivation <span style={{ color: '#cf1322' }}>*</span>
        </div>
        <input
          autoFocus
          placeholder="e.g. Member requested removal, deceased, duplicate entry..."
          value={deactivateReason}
          onChange={e => setDeactivateReason(e.target.value)}
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 13, outline: 'none' }}
        />
        <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
          OK button enables only after entering a reason.
        </div>
      </Modal>

      {/* Main Content */}
      <div style={{ paddingBottom: '24px' }}>
        <Tabs
          defaultActiveKey="1"
          size="small"
          className="dark-tabs"
          style={{
            background: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
          tabBarStyle={{
            background: '#7a0000',
            marginBottom: '0px',
            paddingLeft: 12,
            paddingRight: 12,
          }}
          tabBarGutter={4}
        >
          {/* Personal Details Tab */}
          <TabPane
            tab={
              <Space size="small" style={{ color: 'inherit' }}>
                <UserOutlined />
                <span style={{ fontWeight: '600' }}>Personal Details</span>
              </Space>
            }
            key="1"
          >
            <div style={{ padding: '8px 10px' }}>
              {/* Single unified frame */}
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #dde3ee', overflow: 'hidden' }}>

                {/* ── Personal Information ── */}
                <div style={{ background: '#7a0000', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <UserOutlined style={{ color: '#a8c4e0', fontSize: 12 }} />
                  <span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Personal Information</span>
                </div>
                <div style={{ padding: '16px 18px', borderBottom: '1px solid #e8edf4' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px 20px' }}>
                    {[
                      ['Full Name', userData.name_eng, userData.name_hn],
                      ["Father's Name", userData.father_name_en, userData.father_name_hn],
                      ['Relation', userData.relation_en, userData.relation_hn],
                      ['Date of Birth', userData.dob ? moment(userData.dob).format('DD MMM YYYY') : null, userData.dob ? `Age: ${moment().diff(moment(userData.dob), 'years')} yrs` : null],
                      ['Marital Status', userData.maritial_status, null],
                      ['Caste', userData.caste, null],
                      ['Gotra', userData.gotra, null],
                      ['Kuldevi', userData.kuldevi, null],
                      ['Kulrishi', userData.kulrishi, null],
                      ['Category', userData.category, null],
                    ].map(([label, en, hn], idx) => (
                      <div key={idx}>
                        <div style={{ color: '#6b7b8d', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
                        <div style={{ color: '#7a0000', fontSize: 15, fontWeight: 500 }}>{en || <span style={{ color: '#bbb' }}>—</span>}</div>
                        {hn && <div style={{ color: '#888', fontSize: 13 }}>{hn}</div>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Contact & Identity ── */}
                <div style={{ background: '#7a0000', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <PhoneOutlined style={{ color: '#a8c4e0', fontSize: 12 }} />
                  <span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Contact & Identity</span>
                </div>
                <div style={{ padding: '16px 18px', borderBottom: '1px solid #e8edf4' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px 20px' }}>
                    {[
                      ['Phone', userData.phone_no],
                      ['Email', userData.email_address],
                      ['Alt. Phone 1', userData.alternate_phone_no],
                      ['Alt. Phone 2', userData.alternate_phone_no2],
                      ['PAN Number', userData.pan_number],
                      ['Aadhar No.', userData.aadhar_no ? `**** **** ${userData.aadhar_no.slice(-4)}` : null],
                      ['Joining Date', userData.joining_date ? moment(userData.joining_date).format('DD MMM YYYY') : null],
                      ['Remarks', userData.remark],
                    ].map(([label, val], idx) => (
                      <div key={idx}>
                        <div style={{ color: '#6b7b8d', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
                        <div style={{ color: '#7a0000', fontSize: 15, fontWeight: 500, wordBreak: 'break-all' }}>{val || <span style={{ color: '#bbb' }}>—</span>}</div>
                      </div>
                    ))}
                    <div>
                      <div style={{ color: '#6b7b8d', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Social Media</div>
                      <Space size={8}>
                        {userData.facebook_url && <Tooltip title="Facebook"><a href={userData.facebook_url} target="_blank" rel="noopener noreferrer"><FacebookOutlined style={{ fontSize: 18, color: '#4267B2' }} /></a></Tooltip>}
                        {userData.twitter_url && <Tooltip title="Twitter"><a href={userData.twitter_url} target="_blank" rel="noopener noreferrer"><TwitterOutlined style={{ fontSize: 18, color: '#1da1f2' }} /></a></Tooltip>}
                        {userData.instagram_url && <Tooltip title="Instagram"><a href={userData.instagram_url} target="_blank" rel="noopener noreferrer"><InstagramOutlined style={{ fontSize: 18, color: '#E4405F' }} /></a></Tooltip>}
                        {!userData.facebook_url && !userData.twitter_url && !userData.instagram_url && <span style={{ color: '#bbb', fontSize: 12 }}>—</span>}
                      </Space>
                    </div>
                  </div>
                </div>

                {/* ── Permanent Address ── */}
                <div style={{ background: '#7a0000', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <HomeOutlined style={{ color: '#a8c4e0', fontSize: 12 }} />
                  <span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Permanent Address</span>
                </div>
                <div style={{ padding: '16px 18px', borderBottom: '1px solid #e8edf4' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px 20px' }}>
                    {[
                      ['House / Building', userData.per_address_en, userData.per_address_hn],
                      ['Village', userData.per_village_en, userData.per_village_hn],
                      ['Post', userData.per_post_en, userData.per_post_hn],
                      ['Tahsil', userData.per_tahsil_en, userData.per_tahsil_hn],
                      ['District', userData.per_district_en, userData.per_district_hn],
                      ['State', userData.per_state_en, userData.per_state_hn],
                      ['Country', userData.per_country_en, userData.per_country_hn],
                      ['PIN Code', userData.per_pin_code_en, null],
                    ].map(([label, en, hn], idx) => (
                      <div key={idx}>
                        <div style={{ color: '#6b7b8d', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
                        <div style={{ color: '#7a0000', fontSize: 15, fontWeight: 500 }}>{en || <span style={{ color: '#bbb' }}>—</span>}</div>
                        {hn && en !== hn && <div style={{ color: '#888', fontSize: 13 }}>{hn}</div>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Temporary Address ── */}
                <div style={{ background: '#7a0000', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <HomeOutlined style={{ color: '#a8c4e0', fontSize: 12 }} />
                  <span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Temporary Address</span>
                </div>
                <div style={{ padding: '16px 18px', borderBottom: '1px solid #e8edf4' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px 20px' }}>
                    {[
                      ['House / Building', userData.tem_address_en, userData.tem_address_hn],
                      ['Town / City / Ward', userData.tem_street_en, userData.tem_street_hn],
                      ['City', userData.tem_city_en, userData.tem_city_hn],
                      ['District', userData.tem_district_en, userData.tem_district_hn],
                      ['State', userData.tem_state_en, userData.tem_state_hn],
                      ['Country', userData.tem_country_en, userData.tem_country_hn],
                      ['PIN Code', userData.tem_pin_code_hn, null],
                    ].map(([label, en, hn], idx) => (
                      <div key={idx}>
                        <div style={{ color: '#6b7b8d', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
                        <div style={{ color: '#7a0000', fontSize: 15, fontWeight: 500 }}>{en || <span style={{ color: '#bbb' }}>—</span>}</div>
                        {hn && en !== hn && <div style={{ color: '#888', fontSize: 13 }}>{hn}</div>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Documents ── */}
                <div style={{ background: '#7a0000', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileTextOutlined style={{ color: '#a8c4e0', fontSize: 12 }} />
                  <span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Documents & Attachments</span>
                  <Badge count={multiDocument.length} style={{ backgroundColor: 'rgba(255,255,255,0.2)', marginLeft: 4 }} showZero />
                </div>
                <div style={{ padding: '16px 18px' }}>
                  {multiDocument.length > 0 ? (
                    <Image.PreviewGroup>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {multiDocument.map((item, i) => {
                          const isPdf = item.endsWith('.pdf');
                          return (
                            <div key={i} style={{ width: 80, borderRadius: 6, overflow: 'hidden', border: '1px solid #eee', background: '#fafafa', cursor: 'pointer' }}>
                              {isPdf ? (
                                <div onClick={() => window.open(item, '_blank')} style={{ height: 70, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                                  <img src={PdfImage} alt="PDF" style={{ width: 26, height: 26 }} />
                                  <span style={{ fontSize: 9, color: '#888' }}>PDF {i + 1}</span>
                                </div>
                              ) : (
                                <Image src={item} alt={`Doc ${i + 1}`} style={{ width: '100%', height: 70, objectFit: 'cover' }} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </Image.PreviewGroup>
                  ) : (
                    <div style={{ color: '#bbb', fontSize: 12, padding: '8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FileTextOutlined style={{ fontSize: 20 }} />
                      <span>No documents have been uploaded for this member.</span>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </TabPane>

          {/* Payment History Tab */}
          <TabPane
            tab={
              <Space style={{ color: 'inherit' }}>
                <BankOutlined />
                <span style={{ fontWeight: '600' }}>Payment History</span>
              </Space>
            }
            key="2"
          >
            <div style={{ padding: '10px 12px' }}>
              <UserPaymentModalHistory userid={id} />
            </div>
          </TabPane>

          {/* Occupation Details Tab */}
          <TabPane
            tab={
              <Space style={{ color: 'inherit' }}>
                <IdcardOutlined />
                <span style={{ fontWeight: '600' }}>Occupation</span>
              </Space>
            }
            key="3"
          >
            <div style={{ padding: '10px 12px' }}>
              <OccupationDetails user_id={id} />
            </div>
          </TabPane>

          {/* Family Details Tab */}
          <TabPane
            tab={
              <Space style={{ color: 'inherit' }}>
                <TeamOutlined />
                <span style={{ fontWeight: '600' }}>Family</span>
              </Space>
            }
            key="4"
          >
            <div style={{ padding: '10px 12px' }}>
              <FamilyDetails user_id={id} />
            </div>
          </TabPane>

          {/* Social Status Tab */}
          <TabPane
            tab={
              <Space style={{ color: 'inherit' }}>
                <UserOutlined />
                <span style={{ fontWeight: '600' }}>Social Status</span>
              </Space>
            }
            key="5"
          >
            <div style={{ padding: '10px 12px' }}>
              <SocialStatus user_id={id} />
            </div>
          </TabPane>
        </Tabs>
      </div>

      {/* Profile Image Modal */}
      <Modal
        open={isModalVisible}
        footer={null}
        onCancel={handleCancel}
        centered
        width="auto"
        style={{ maxWidth: '90vw' }}
        styles={{
          body: { padding: '24px' }
        }}
      >
        <img
          src={currentImage}
          alt="Profile"
          style={{ 
            width: '100%', 
            maxHeight: '80vh', 
            objectFit: 'contain',
            borderRadius: '12px'
          }}
        />
      </Modal>

      {/* Custom Styles */}
      <style jsx>{`
        .ant-descriptions-item-label {
          font-weight: 600 !important;
          color: #555 !important;
        }
        
        .ant-descriptions-item-content {
          font-weight: 400 !important;
        }
        
        .ant-tabs-tab {
          font-weight: 600 !important;
          padding: 16px 24px !important;
          border-radius: 8px 8px 0 0 !important;
          transition: all 0.3s ease !important;
        }
        
        .ant-tabs-tab-active {
          background: #7a0000 !important;
          color: white !important;
        }

        .ant-tabs-tab:hover {
          background: rgba(26, 46, 74, 0.08) !important;
        }
        
        .ant-card {
          transition: all 0.3s ease !important;
        }
        
        .ant-card:hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 12px 32px rgba(0,0,0,0.12) !important;
        }
        
        .ant-progress-circle .ant-progress-text {
          font-weight: 600 !important;
        }
        
        .ant-tag {
          border-radius: 8px !important;
          font-weight: 500 !important;
          border: 1px solid rgba(0,0,0,0.1) !important;
        }
        
        .ant-avatar {
          transition: all 0.3s ease !important;
        }
        
        .ant-avatar:hover {
          transform: scale(1.05) !important;
          box-shadow: 0 16px 48px rgba(0,0,0,0.4) !important;
        }
        
        .ant-descriptions-item {
          padding-bottom: 20px !important;
        }
        
        .ant-space-item {
          display: flex !important;
          align-items: center !important;
        }
        
        @media (max-width: 768px) {
          .ant-col {
            margin-bottom: 16px !important;
          }
          
          .ant-tabs-tab {
            padding: 12px 16px !important;
            font-size: 14px !important;
          }
          
          .ant-descriptions {
            font-size: 14px !important;
          }
          
          .ant-descriptions-item-label {
            width: 45% !important;
          }
        }
        
        @media (max-width: 576px) {
          .ant-avatar {
            width: 120px !important;
            height: 120px !important;
          }
          
          .ant-typography h1 {
            font-size: 24px !important;
          }
          
          .ant-card {
            margin-bottom: 16px !important;
          }
        }
        
        .document-card:hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 8px 24px rgba(0,0,0,0.15) !important;
        }
        
        .gradient-header {
          background: linear-gradient(135deg, #7a0000 0%, #2c4a6e 100%) !important;
        }

        .glass-card {
          background: rgba(255,255,255,0.15) !important;
          backdrop-filter: blur(20px) !important;
          border: 1px solid rgba(255,255,255,0.2) !important;
        }
        
        .info-section {
          background: linear-gradient(90deg, #f0f3f7 0%, #e8edf4 100%) !important;
        }
        
        .status-active {
          background: linear-gradient(90deg, #f6ffed 0%, #f0fff0 100%) !important;
        }
        
        .status-inactive {
          background: linear-gradient(90deg, #fff2f0 0%, #fff1f0 100%) !important;
        }
        
        .status-pending {
          background: linear-gradient(90deg, #fffbe6 0%, #fffff0 100%) !important;
        }
      `}</style>
    </div>
  );
}

export default View;